import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {
  ChevronRight, LogOut, Bell, Menu, Search, Sparkles, X,
} from 'lucide-react';
import { useToast, ThemeToggle } from '@kollez/design-system';
import { ROLE_SHELL, type Shell } from '@kollez/shared';
import { NAV, SHELL_LABEL } from '@/config/nav';
import { NAV_ICONS } from '@/config/navIcons';
import { useAuthStore } from '@/lib/authStore';
import { useNotifications } from '@/lib/queries';
import { useNotificationStream } from '@/lib/useNotificationStream';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

/**
 * AppShell (F77) — direct port of kollez/assets/shell.js:
 *   .app grid  → sidebar (244px) + main column
 *   sidebar:   .sb-brand · .sb-workspace · .sb-nav (.sb-group / .sb-link / .sb-count) · .sb-foot
 *   topbar:    hamburger · .crumbs · spacer · .kbar (⌘K trigger) · nexie · bell · avatar
 *   bottom-nav (student, <900px)
 *
 * Class contracts are declared in packages/design-system/src/styles/kollez.css — matching them
 * one-to-one is what lets themes + hover states + dark mode work without extra CSS.
 */

// Wordmark from shell.js (Newsreader inline)
function Wordmark() {
  return (
    <span style={{ fontFamily: 'Newsreader, Georgia, serif', fontSize: 21, fontWeight: 600, letterSpacing: '-.02em' }}>
      kollez<span style={{ color: 'rgb(var(--ink-tertiary))', fontWeight: 400 }}>.ai</span>
    </span>
  );
}

function Logo() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
      <rect x="0.5" y="0.5" width="25" height="25" rx="6" fill="rgb(var(--bg-inkwell))" stroke="rgb(var(--line-secondary))" />
      <path d="M8 3v20" stroke="rgb(var(--margin))" strokeWidth="1.5" />
      <path d="M11 9.5h10M11 14h10M11 18.5h6" stroke="#fff" strokeOpacity=".85" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// Route → breadcrumb trail. Falls back to the shell label + last URL segment.
function useCrumbs(shell: Shell): string[] {
  const loc = useLocation();
  return useMemo(() => {
    const seg = loc.pathname.split('/').filter(Boolean);
    const last = seg.length > 0 ? seg[seg.length - 1]!.replace(/-/g, ' ') : SHELL_LABEL[shell];
    return [last.charAt(0).toUpperCase() + last.slice(1)];
  }, [loc.pathname, shell]);
}

function initials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
}

export function AppShell() {
  const { user, loading, logout } = useAuthStore();
  const nav = useNavigate();
  const loc = useLocation();
  const toast = useToast();
  const notif = useNotifications();
  const [sbOpen, setSbOpen] = useState(false);

  useNotificationStream((ev) => {
    toast.show({ title: ev.title, message: ev.body });
  });

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/login', { replace: true }); return; }
    if (!user.onboardingComplete) nav('/onboarding', { replace: true });
  }, [user, loading, nav]);

  const shell: Shell = user
    ? (ROLE_SHELL[user.role as keyof typeof ROLE_SHELL] ?? 'student')
    : 'student';
  const crumbs = useCrumbs(shell);

  // Do not render the app tree while auth is still hydrating or the user is unknown — the
  // useEffect above will redirect, and rendering an empty tree that then swaps to a redirected
  // route causes React's reconciler to lose track of DOM nodes (F-024: removeChild loops).
  if (loading || !user) {
    return <div className="app" style={{ minHeight: '100vh' }} aria-busy="true" />;
  }

  // From here `user` and `!loading` are guaranteed by the guard above.
  const unread = notif.data?.unread ?? 0;

  const groups = NAV[shell];
  const label = SHELL_LABEL[shell];

  async function handleLogout() {
    await logout();
    nav('/login', { replace: true });
  }

  // Role-specific quick action in the sidebar foot, per shell.js
  const quickAction = shell === 'tpo'
    ? { href: '/tpo/drives/new', label: 'Post a drive' }
    : shell === 'company'
    ? { href: '/company/openings/new', label: 'New opening' }
    : null;

  return (
    <div className="app">
      {/* Sidebar */}
      <nav className="sidebar" data-open={sbOpen ? 'true' : 'false'} aria-label="Main">
        <div className="sb-brand">
          {/* Step 67 — brand mark routes to the shell's own home (student → /home, TPO →
              /tpo/today, etc.), not marketing `/`. A signed-in user tapping the brand
              expects to land on their dashboard, not the public landing page. Also swaps
              raw <a href> → <NavLink> so nav stays SPA. */}
          <NavLink
            to={groups[0]?.items[0]?.href ?? '/'}
            style={{ display: 'flex', alignItems: 'center', gap: 9 }}
            aria-label="kollez.ai home"
          >
            <Logo /><Wordmark />
          </NavLink>
          <button
            className="btn btn-ghost btn-sm btn-icon show-mobile"
            style={{ marginLeft: 'auto' }}
            onClick={() => setSbOpen(false)}
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>

        <WorkspaceSwitcher fallbackLabel={label} userName={user.fullName} />

        <div className="sb-nav">
          {groups.map((g, gi) => (
            <div key={gi}>
              {g.label ? <div className="sb-group">{g.label}</div> : <div style={{ height: 6 }} />}
              {g.items.map((it) => {
                const Glyph = it.icon ? NAV_ICONS[it.icon] : undefined;
                return (
                  <NavLink
                    key={it.key}
                    to={it.href}
                    className={({ isActive }) => `sb-link${isActive ? ' active' : ''}`}
                    aria-current={undefined}
                    onClick={() => setSbOpen(false)}
                  >
                    {({ isActive }) => (
                      <>
                        <span aria-current={isActive ? 'page' : undefined} style={{ display: 'contents' }} />
                        {Glyph && <Glyph size={16} aria-hidden="true" />}
                        <span>{it.label}</span>
                        {typeof it.count === 'number' && it.count > 0 && (
                          <span className="sb-count">{it.count}</span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </div>

        <div className="sb-foot" style={{ display: 'grid', gap: 10 }}>
          {quickAction && (
            <NavLink
              className="btn btn-primary"
              style={{ width: '100%' }}
              to={quickAction.href}
            >
              + {quickAction.label}
            </NavLink>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 4 }}>
            <span className="avatar">{initials(user.fullName)}</span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span className="truncate" style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{user.fullName}</span>
              <span className="truncate" style={{ display: 'block', fontSize: 12, color: 'rgb(var(--ink-tertiary))' }}>{user.role}</span>
            </span>
            <button className="btn btn-ghost btn-sm btn-icon" onClick={handleLogout} aria-label="Sign out">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </nav>

      {/* scrim for mobile sidebar */}
      <div className="scrim" data-open={sbOpen ? 'true' : 'false'} onClick={() => setSbOpen(false)} />

      {/* Main column */}
      <div style={{ minWidth: 0 }}>
        <header className="topbar">
          <button className="btn btn-ghost btn-icon tb-menu" aria-label="Open menu" onClick={() => setSbOpen(true)}>
            <Menu size={16} />
          </button>
          <div className="crumbs" aria-label="Breadcrumb">
            <span className="hide-mobile" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <span>{label}</span>
              <ChevronRight size={12} />
            </span>
            {crumbs.map((c, i) => i === crumbs.length - 1 ? (
              <span key={i} className="here">{c}</span>
            ) : (
              <span key={i} className="hide-mobile">
                {c}<ChevronRight size={12} />
              </span>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <button className="kbar" type="button" aria-label="Command palette">
              <Search size={14} />
              <span style={{ flex: 1, textAlign: 'left' }}>Search or jump to…</span>
              <kbd>⌘K</kbd>
            </button>
            <button className="btn btn-ghost btn-icon show-mobile" aria-label="Search">
              <Search size={16} />
            </button>
            <a className="btn btn-ghost btn-icon hide-mobile" href="/nexie" aria-label="Ask Nexie">
              <Sparkles size={16} />
            </a>
            <ThemeToggle />
            <button
              className="btn btn-ghost btn-icon"
              style={{ position: 'relative' }}
              onClick={() => nav('/notifications')}
              aria-label="Notifications"
            >
              <Bell size={16} />
              {unread > 0 && (
                <span
                  aria-label={`${unread} unread ${unread === 1 ? 'notification' : 'notifications'}`}
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                    borderRadius: 8,
                    background: 'rgb(var(--ink-danger))',
                    color: 'rgb(var(--ink-inverse))',
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: '16px',
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>
            <NavLink
              className="avatar show-mobile"
              style={{ marginLeft: 4, textDecoration: 'none' }}
              to="/settings/privacy"
              aria-label="Your account"
            >
              {initials(user.fullName)}
            </NavLink>
          </div>
        </header>
        <main id="k-main" className="page-container-wide" style={{ paddingBottom: shell === 'student' ? '96px' : '24px', paddingTop: '20px' }}>
          <Outlet />
        </main>
      </div>

      {shell === 'student' && <BottomNav pathname={loc.pathname} />}
    </div>
  );
}

function BottomNav({ pathname: _pathname }: { pathname: string }) {
  // Step 65 — bottom-nav must use SPA links, not raw <a href>. A raw anchor triggers a
  // full-page reload on every tap (bad on the mobile shells this nav actually serves) and
  // drops in-memory auth state — the student would re-authenticate on every navigation.
  // <NavLink> keeps the router in charge and auto-derives aria-current.
  const items: Array<[string, string]> = [
    ['/home', 'Home'],
    ['/explore', 'Explore'],
    ['/applications', 'Applied'],
    ['/profile', 'Profile'],
    ['/nexie', 'Nexie'],
  ];
  return (
    <nav className="bottomnav" aria-label="Primary">
      {items.map(([href, label]) => (
        <NavLink key={href} to={href}>
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
